import { useEffect, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { useLDClient, useFlags } from "launchdarkly-react-client-sdk";
import { EMPTY_CONTEXT } from "../../constants/context";

/**
 * Base hook for boolean feature flag evaluation via LaunchDarkly.
 *
 * Portal-specific hooks should wrap this, resolving their own `entityId` and
 * `userContext` from their respective auth state before calling this hook.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param entityId The user/entity ID for flag targeting; returns disabled if undefined
 * @param userContext Sanitized user attributes to include in the LD context
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify)
 * @param onAnalytics Optional callback invoked after evaluation for portal-specific analytics
 */
export const useBaseBooleanFeatureFlag = (
  flagKey: string,
  entityId: string | undefined,
  userContext: Record<string, unknown>,
  additionalContext: Record<string, unknown> = EMPTY_CONTEXT,
  onAnalytics?: (enabled: boolean) => void,
): { enabled: boolean; loading: boolean } => {
  const ldClient = useLDClient();
  const flags = useFlags();
  const flagValue = flags[flagKey];
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Stable ref so the analytics callback never needs to be in the effect deps.
  const onAnalyticsRef = useRef(onAnalytics);
  useEffect(() => {
    onAnalyticsRef.current = onAnalytics;
  });

  useEffect(() => {
    const evaluateFlag = async () => {
      let evalEnabled = false;

      if (!ldClient || !entityId) {
        setEnabled(false);
        setLoading(false);
        return;
      }

      try {
        if (Object.keys(additionalContext).length > 0) {
          setLoading(true);
          const baseContext = {
            kind: "user",
            key: entityId,
            ...userContext,
            ...additionalContext,
          };
          await ldClient.identify(baseContext);
          evalEnabled = ldClient.variation(flagKey, false);
        } else {
          evalEnabled = flagValue || false;
        }
        setEnabled(evalEnabled);
      } catch (error) {
        console.error(`Error evaluating LaunchDarkly flag ${flagKey}:`, error);
        evalEnabled = false;
        setEnabled(evalEnabled);
      } finally {
        setLoading(false);
      }

      onAnalyticsRef.current?.(evalEnabled);
      datadogRum.addFeatureFlagEvaluation(flagKey, evalEnabled);
    };

    evaluateFlag();
  }, [ldClient, flagValue, flagKey, entityId, userContext, additionalContext]);

  return { enabled, loading };
};

/**
 * Base hook for variant (string-valued) feature flag evaluation via LaunchDarkly.
 *
 * Portal-specific hooks should wrap this, resolving their own `entityId` and
 * `userContext` from their respective auth state before calling this hook.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param entityId The user/entity ID for flag targeting; returns empty string if undefined
 * @param userContext Sanitized user attributes to include in the LD context
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify)
 * @param onAnalytics Optional callback invoked after evaluation for portal-specific analytics
 */
export const useBaseVariantFeatureFlag = (
  flagKey: string,
  entityId: string | undefined,
  userContext: Record<string, unknown>,
  additionalContext: Record<string, unknown> = EMPTY_CONTEXT,
  onAnalytics?: (variant: string) => void,
): { variant: string; loading: boolean } => {
  const ldClient = useLDClient();
  const flags = useFlags();
  const flagValue = flags[flagKey];
  const [variant, setVariant] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const onAnalyticsRef = useRef(onAnalytics);
  useEffect(() => {
    onAnalyticsRef.current = onAnalytics;
  });

  useEffect(() => {
    const evaluateFlag = async () => {
      let evalVariant = "";

      if (!ldClient || !entityId) {
        setVariant("");
        setLoading(false);
        return;
      }

      try {
        if (Object.keys(additionalContext).length > 0) {
          setLoading(true);
          const baseContext = {
            kind: "user",
            key: entityId,
            ...userContext,
            ...additionalContext,
          };
          await ldClient.identify(baseContext);
          evalVariant = ldClient.variation(flagKey, "");
        } else {
          evalVariant = flagValue || "";
        }
        setVariant(evalVariant);
      } catch (error) {
        console.error(`Error evaluating LaunchDarkly flag ${flagKey}:`, error);
        evalVariant = "";
        setVariant(evalVariant);
      } finally {
        setLoading(false);
      }

      onAnalyticsRef.current?.(evalVariant);
      datadogRum.addFeatureFlagEvaluation(flagKey, evalVariant);
    };

    evaluateFlag();
  }, [ldClient, flagValue, flagKey, entityId, userContext, additionalContext]);

  return { variant, loading };
};
